// GenerateBodyAnimWizard — text prompt → kimodo motion → first-class body
// animation asset. Generate previews live on the avatar; Save bakes the last
// generation into a bones-only GLB and uploads it. Rendered docked in the
// content browser so the viewport stays visible.

import React, { useState, useEffect, useRef } from 'react'
import { inputStyle, labelStyle, tokens } from './WizardShell'
import { CustomAssetManager } from '../../../lib/CustomAssetManager'
import { assetRegistry } from '../../../lib/assetRegistry'
import { postGenerate, playTracks, stopPreview, genBodyMotionOnWaypoints, currentAvatarXZ, currentAvatarFacingXZ } from '../../../lib/bodyMotionNav'
import { exportBodyMotionGlb, downloadBodyMotionGlb } from '../../../lib/bodyMotionExport'
import { waypointPickerStore } from '../../../lib/waypointPickerStore'
import { avatarFloor } from '../../../lib/avatarFloor'
import { planWaypointRoute, clampRouteDuration, constraintFrames, waypointFrameIndices, DEFAULT_CONSTRAINT_STRIDE, DEFAULT_CONSTRAINT_WEIGHT, DEFAULT_SMOOTH, routeHeadingPreview } from '../../../lib/bodyMotionPath'
import { durationOnEnterDurationMode } from '../../../lib/bodyMotionTiming'
import { WAYPOINT_COLORS } from '../../../lib/waypointColors'
import FloatingWindow, { useWindowState } from '../../FloatingWindow'

const randomSeed = () => Math.floor(Math.random() * 0x7fffffff)

// One window id for both the Generate and Modify flows: it is the same panel
// doing the same job, so a user who floated it once should not have it snap
// back to the dock the first time they reopen it via Modify.
export const GENERATE_WINDOW_ID = 'generate-body-anim'

export default function GenerateBodyAnimWizard({
  path = [],
  onDone,
  onCancel,
  width = 360,
  initialValues = null,
  modifyTarget = null,
  // True when the shell's right Inspector panel is the docked host (the panel
  // owns the box, so this panel must not also impose a fixed-width column).
  hosted = false,
}) {
  const iv = initialValues ?? {}
  // Empty by default so the author must name the clip (no silent 'generated_motion'
  // collisions); Modify prefills from the asset row.
  const [name, setName] = useState(iv.name ?? '')
  const [prompt, setPrompt] = useState(iv.prompt ?? '')
  const [durationSec, setDurationSec] = useState(iv.duration_sec != null ? String(iv.duration_sec) : '4')
  // Has the author set the duration themselves — typed it, or brought one in
  // through a Modify round-trip (provenance duration_sec)? "By duration" only
  // pre-seeds the field from the route-derived value while this is false;
  // re-seeding an explicit value on every pill click silently discarded it.
  const [durationTouched, setDurationTouched] = useState(iv.duration_sec != null)
  const [seed, setSeed] = useState(iv.seed != null ? String(iv.seed) : '')
  const [diffusionSteps, setDiffusionSteps] = useState(iv.diffusion_steps != null ? String(iv.diffusion_steps) : '100')
  // The flag the file stores is `keepRootMotion`; the flag the user sets is its INVERSE.
  // Dropping the pelvis track takes a jump's rise as well as a walk's travel, so the
  // motion people generate should keep it unless they say otherwise — and a default-off
  // "In place" says that plainly, where a default-off "Keep root motion" said the reverse.
  // `=== false`, not `!`: only an asset SAVED in place re-opens ticked; a fresh
  // generation, or provenance with no such field, stays off.
  const [inPlace, setInPlace] = useState(iv.keepRootMotion === false)
  const keepRootMotion = !inPlace
  // `category` is carried, not authored. Nothing in the stack branches on it
  // (ENG-1046: free-text column, no server validation, no consumer in
  // convorcher or the AAM) so the wizard no longer asks — but a Modify
  // round-trip MUST preserve whatever tag the asset already has, or re-saving a
  // 'gesture' clip would silently retag it. Fresh clips get the server default.
  const category = iv.category ?? 'other'
  const [loopable, setLoopable] = useState(!!iv.loopable)
  // Additive gesture clips are baked deltas-from-rest at load so GestureChannel
  // can layer them over the base pose (it flips blendMode on the raw action and
  // never bakes itself — an un-baked full-pose clip played additively inflates
  // the character). Off by default; the author opts in for a gesture-channel
  // clip (mirrors BodyAnimWizard).
  const [additive, setAdditive] = useState(!!iv.additive)
  // Avg walk speed (m/s). Drives the route's derived clip duration
  // (duration = route length / speed). Editable in the Waypoints section and
  // round-trips through Modify. Kept as text so a mid-edit empty field doesn't
  // snap to NaN; the effective value is clamped to a sane minimum.
  const [walkSpeedInput, setWalkSpeedInput] = useState(
    iv.walk_speed != null && iv.walk_speed > 0 ? String(iv.walk_speed) : '1.3',
  )
  const walkSpeed = Math.max(0.1, Number(walkSpeedInput) || 1.3)

  // Path tracking (the root2d constraint's two knobs), authored per clip and
  // round-tripped through Modify. Both were hardcoded defaults until now, which
  // made the one thing you'd actually tune when a generated walk fights its
  // route — how hard it is pinned to the polyline — the one thing you couldn't.
  //   stride: anchor every N of the 30 fps frames. 1 pins every frame to a
  //     navmesh polyline that is itself a 0.25 m staircase; larger strides let
  //     the model interpolate its own gait between anchors (smoother facing,
  //     looser tracking). Waypoints and endpoints are pinned at every stride.
  //   weight: root2d CFG scale. Raising it tracks harder, but the constraint
  //     carries facing as well as position, so past ~2 it visibly fights the gait.
  // Text, not numbers, so a mid-edit empty field doesn't snap to NaN; the
  // effective values are floored/rounded the same way the generator does.
  const [strideInput, setStrideInput] = useState(
    iv.constraint_stride != null ? String(iv.constraint_stride) : String(DEFAULT_CONSTRAINT_STRIDE),
  )
  const [weightInput, setWeightInput] = useState(
    iv.constraint_weight != null ? String(iv.constraint_weight) : String(DEFAULT_CONSTRAINT_WEIGHT),
  )
  // Parsed with an explicit isFinite check, not `|| default`: weight 0 is a
  // meaningful setting (generate unguided, for an A/B against the constrained
  // run) and `0 || 1` would silently swallow it into the default.
  const numOr = (raw, fallback) => (raw.trim() !== '' && Number.isFinite(Number(raw)) ? Number(raw) : fallback)
  const constraintStride = Math.max(1, Math.round(numOr(strideInput, DEFAULT_CONSTRAINT_STRIDE)))
  // kimodo accepts 0 < x <= 20; a literal 0 is a 422, so the field cannot offer it.
  const constraintWeight = Math.min(20, Math.max(0.01, numOr(weightInput, DEFAULT_CONSTRAINT_WEIGHT)))

  // Route SHAPE. The A* grid is 4-connected, so a diagonal plans as a Manhattan
  // staircase: measured on a 0.25 m grid, an 8.5 m diagonal comes back 12.0 m
  // long with the heading flipping 0/90 degrees 69 times. 'curve' rounds each
  // TURN into a Bezier fillet and leaves the straight runs alone
  // (smoothRoutePath). Waypoints are turns like any other and round too.
  const [smooth, setSmooth] = useState(iv.smooth ?? DEFAULT_SMOOTH)
  // Path tracking is the tuning half of this section: sensible defaults, and a
  // reason to reach for them only when a generated walk misbehaves. Collapsed
  // behind Advanced so the common path is Shape + waypoints and nothing else.
  // Auto-opened for a Modify round-trip that carries non-default values, so a
  // clip authored with them does not reopen looking like it was not.
  const [advanced, setAdvanced] = useState(
    iv.constraint_stride != null || iv.constraint_weight != null,
  )

  // modifyTarget ({ id, name, path }) set → Save offers "Save as new" or "Replace
  // original". Stable across renders via the key= remount in ContentBrowser.
  const [saveMode, setSaveMode] = useState('new') // 'new' | 'replace'

  // Window mode (docked | floating | popped) — persisted per id by windowStore,
  // so a panel the user floated stays floated across opens and reloads.
  const win = useWindowState(GENERATE_WINDOW_ID)

  // Last successful generation, so Save works on the exact previewed motion.
  const [lastTracks, setLastTracks] = useState(null)
  const [lastGen, setLastGen] = useState(null) // { seed, duration_sec, diffusion_steps, prompt, generatedAt }
  // Per-waypoint reached readout from the last waypoint generation (null for the
  // prompt-only path). [{ index, dist, reached }, …].
  const [lastPerWaypoint, setLastPerWaypoint] = useState(null)

  // Waypoint picker is a module singleton (shared with the in-Canvas overlay).
  // Subscribe so the wizard re-renders when the list / mode / route / pick-error
  // changes (NavMeshVolume precedent). The waypoint LIST is cold user-cadence
  // state — module singleton, not React state — so the overlay reads the same.
  const [, force] = useState(0)
  useEffect(() => waypointPickerStore.subscribe(() => force(n => n + 1)), [])
  const wpState = waypointPickerStore.getState()

  // Poll navmesh presence while the wizard is open so the mode toggle reflects
  // reality — the navmesh can bake/load after the wizard opens. Cheap, wizard-scoped.
  const [hasNavmesh, setHasNavmesh] = useState(() => typeof window !== 'undefined' && !!window.__NAVMESH_INSTANCE__)
  useEffect(() => {
    const check = () => setHasNavmesh(typeof window !== 'undefined' && !!window.__NAVMESH_INSTANCE__)
    check()
    const id = setInterval(check, 1000)
    return () => clearInterval(id)
  }, [])
  // With no navmesh baked, navmesh mode can neither plan nor pick — force the
  // explicit straight-line mode so the option is never a dead end that errors.
  useEffect(() => {
    if (!hasNavmesh && waypointPickerStore.getState().pathMode === 'navmesh') {
      waypointPickerStore.setPathMode('straight')
    }
  }, [hasNavmesh])

  // Waypoints section is optional → collapsed by default; auto-expanded when a
  // Modify round-trip brings waypoints in.
  const [wpCollapsed, setWpCollapsed] = useState(!(Array.isArray(iv.waypoints) && iv.waypoints.length))
  // Route timing knob: 'speed' derives duration from route length ÷ avg speed;
  // 'duration' pins an explicit clip length (speed then ignored). Mutually
  // exclusive. Round-trips through Modify (persisted in provenance) so a
  // duration-pinned clip reopens pinned instead of silently re-deriving.
  const [timingMode, setTimingMode] = useState(iv.timing_mode ?? 'speed')
  // Original avatar group XZ, captured the first time a waypoint preview relocates
  // the avatar to the route start, so we can put it back when the wizard closes.
  const originalGroupXZRef = useRef(null)

  // Manual waypoint entry (X/Z in world meters) — an alternative to viewport picking.
  const [manualX, setManualX] = useState('')
  const [manualZ, setManualZ] = useState('')
  const addManualWaypoint = () => {
    const x = Number(manualX), z = Number(manualZ)
    if (!Number.isFinite(x) || !Number.isFinite(z) || manualX.trim() === '' || manualZ.trim() === '') {
      setError('Waypoint X and Z must both be numbers'); return
    }
    setError(null)
    waypointPickerStore.addWaypoint({ x, z, snapped: false, drift: 0 })
    setManualX(''); setManualZ('')
  }

  // Seed the picker from a Modify round-trip, then tear it down on unmount so a
  // discarded wizard doesn't leave the viewport armed / markers lingering.
  useEffect(() => {
    if (Array.isArray(iv.waypoints) && iv.waypoints.length) {
      waypointPickerStore.setWaypoints(iv.waypoints.map(([x, z]) => ({ x, z, snapped: false, drift: 0 })))
    }
    // Apply the seeded mode only when it's usable NOW: 'straight' always;
    // 'navmesh' only if one is loaded. Otherwise the force-straight effect above
    // owns the mode — seeding a disabled 'navmesh' here would strand the pill and
    // block Generate (the very dead end that effect exists to prevent).
    if (iv.path_mode === 'straight' ||
        (iv.path_mode === 'navmesh' && typeof window !== 'undefined' && window.__NAVMESH_INSTANCE__)) {
      waypointPickerStore.setPathMode(iv.path_mode)
    }
    // Reset pathMode too (clear() deliberately keeps it) so a Modify-seeded or
    // no-navmesh 'straight' doesn't leak into the next fresh wizard.
    return () => { waypointPickerStore.disarm(); waypointPickerStore.clear(); waypointPickerStore.setPathMode('navmesh') }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Escape disarms the picker (leaves the placed waypoints intact).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && waypointPickerStore.getState().armed) waypointPickerStore.disarm() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Attach the facings the request will carry, for the viewport preview. Built
  // by routeHeadingPreview from the SAME builder genBodyMotionOnWaypoints uses,
  // so the arrows on screen are the headings actually sent — the wizard is the
  // right place for it because the duration depends on this panel's own speed /
  // timing state, which the overlay has no business knowing.
  const withHeadings = (plan, pts) => {
    if (!plan || plan.error || !(plan.path?.length >= 2)) return plan
    try {
      const dur = timingMode === 'duration'
        ? Math.max(0.1, Number(durationSec) || 4)
        : clampRouteDuration(plan.length, walkSpeed)
      return {
        ...plan,
        headings: routeHeadingPreview(plan.path, pts, {
          durationSec: dur,
          stride: constraintStride,
          startFacing: currentAvatarFacingXZ(),
          // Every anchor: these dots ARE the root_xz array kimodo receives.
          every: 1,
        }),
      }
    } catch (e) {
      // A preview is never worth breaking the route over.
      console.warn('[generate] heading preview failed:', e)
      return plan
    }
  }

  // Cold route re-plan on list / mode change (user cadence, never per-frame).
  // Depend ONLY on the inputs — reading route here would loop (setRoute re-emits).
  useEffect(() => {
    const wps = wpState.waypoints
    if (wps.length < 2) { waypointPickerStore.setRoute(null); return }
    const pts = wps.map(w => [w.x, 0, w.z])
    if (wpState.pathMode === 'navmesh') {
      // The navmesh can finish loading AFTER waypoints are placed. Plan once now;
      // if it isn't ready yet, show the state but keep polling so the route (and
      // Generate) unblock automatically once it arrives — otherwise the stale
      // "navmesh not loaded" error wedges Generate until the user touches the list.
      let poll = null
      const plan = () => {
        const nm = typeof window !== 'undefined' ? window.__NAVMESH_INSTANCE__ : null
        if (!nm) {
          // Same message every 500 ms tick — only re-emit if it actually changed,
          // else each poll mints a fresh route object and forces a full overlay
          // marker rebuild + wizard re-render twice a second while unbaked.
          const msg = 'navmesh not loaded — switch to straight-line mode or bake a navmesh'
          if (waypointPickerStore.getState().route?.error !== msg) waypointPickerStore.setRoute({ error: msg })
          return false
        }
        try { waypointPickerStore.setRoute(withHeadings(planWaypointRoute(nm, pts, { mode: 'navmesh', smooth }), pts)) }
        catch (e) { waypointPickerStore.setRoute({ error: String(e.message ?? e) }) }
        return true
      }
      if (!plan()) poll = setInterval(() => { if (plan()) { clearInterval(poll); poll = null } }, 500)
      return () => { if (poll) clearInterval(poll) }
    }
    waypointPickerStore.setRoute(withHeadings(planWaypointRoute(null, pts, { mode: 'straight', smooth }), pts))
    // `smooth` is a dependency: the route line in the viewport IS the preview of
    // this control, so changing the shape must re-plan and redraw immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // durationSec / walkSpeed / timingMode / stride feed the heading preview,
    // so the arrows follow the panel rather than going stale.
  }, [wpState.waypoints, wpState.pathMode, smooth, walkSpeed, timingMode, durationSec, constraintStride])

  // Route timing: 'speed' derives duration from route length ÷ avg speed;
  // 'duration' pins an explicit clip length. Mutually exclusive — exactly one is
  // active when a route is set. Derived duration drives the read-only display.
  const wpActive = wpState.waypoints.length >= 2
  const routeLen = wpState.route && !wpState.route.error ? wpState.route.length : null
  const derivedDuration = routeLen != null ? clampRouteDuration(routeLen, walkSpeed) : null
  const timingByDuration = wpActive && timingMode === 'duration'
  const timingBySpeed = wpActive && timingMode === 'speed'

  const [error, setError] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const busy = generating || saving || downloading

  // Closing the panel stops the live preview so a discarded generation doesn't
  // keep playing on the avatar until its envelope self-clears.
  const handleCancel = () => { waypointPickerStore.disarm(); stopPreview(); onCancel?.() }

  // Put the avatar back where it stood before a waypoint preview relocated it to
  // the route start (below). Restore on close so the wizard leaves no side-effect.
  useEffect(() => () => {
    const g = typeof window !== 'undefined' ? window.__AVATAR_GROUP__ : null
    const o = originalGroupXZRef.current
    if (g && o) {
      g.position.x = o[0]
      g.position.z = o[1]
      if (Number.isFinite(o[2])) g.rotation.y = o[2]
    }
  }, [])

  // Preview a set of tracks live on the avatar (kimodo_generated slot).
  // commitRootDelta:null so a Generate/Re-roll/Preview-again loop doesn't walk
  // the avatar farther across the scene each iteration — the previewed motion
  // returns to base each time.
  // keepRootMotion: the preview plays what SAVE would store. Without it the preview
  // always extracted the root — the avatar group carrying the travel, the pelvis the
  // vertical — so an "In place" clip was watched jumping and walking and then stored
  // doing neither, with nothing on screen saying so. Both preview entry points come
  // through here, so this is the only place that has to pass it.
  const previewTracks = (tracks) => playTracks(tracks, { commitRootDelta: null, keepRootMotion })

  // Waypoint preview: relocate the avatar to the route's world start (tracks.root
  // frame 0) so the walk overlays the actual waypoints/route line in the viewport
  // instead of starting wherever the avatar happens to stand. Still returns to the
  // start each time (commitRootDelta:null), so re-previews don't drift. The
  // pre-preview position is captured once and restored on close (effect above).
  // `startWorld` is the route's own first point in WORLD coordinates, taken from
  // the generation result — NOT from tracks.root. The tracks are in the
  // character's frame (they are played inside the avatar group, which supplies
  // the rotation), so tracks.root[0] is the origin of that frame, near zero.
  // Reading it as a world position parked the avatar at the world origin instead
  // of at waypoint 0, and the walk then started from wherever that happened to be.
  const previewWaypointTracks = (tracks, startWorld, frameYaw) => {
    const g = typeof window !== 'undefined' ? window.__AVATAR_GROUP__ : null
    if (g && Array.isArray(startWorld)) {
      if (originalGroupXZRef.current == null) {
        originalGroupXZRef.current = [g.position.x, g.position.z, g.rotation.y]
      }
      g.position.x = startWorld[0]
      g.position.z = startWorld[2]
      // Turn the group to the frame the clip was generated in. An authored clip
      // sets off straight ahead in its OWN frame, so the group carries the
      // route's direction — that is what puts the walk back in the world, and
      // what stops the body turning through the opening angle as the clip
      // crossfades in over whatever pose was running.
      if (Number.isFinite(frameYaw)) g.rotation.y = frameYaw
    }
    previewTracks(tracks)
  }

  const runGenerate = async (seedValue) => {
    if (!prompt.trim()) { setError('Prompt is required'); return }
    setGenerating(true); setError(null)
    try {
      // Clamp to sane minimums — a blank field falls back to the default, and a
      // negative value (truthy, so `|| default` wouldn't catch it) is floored.
      const steps = Math.max(1, Math.round(Number(diffusionSteps) || 100))
      const durSec = Math.max(0.1, Number(durationSec) || 4)
      const wps = waypointPickerStore.getState()
      if (wps.waypoints.length >= 2) {
        // Waypoint route: one concatenated path → one kimodo call. Timing is
        // either an explicit duration_sec (duration mode) or route-derived from
        // walk speed (speed mode) — never both.
        // World floor, not the navmesh's own floorY (which is the bake space's
        // floor height — see ViewportContextMenu's note).
        const floorY = avatarFloor.getY()
        const passDuration = timingMode === 'duration' ? durSec : undefined
        const res = await genBodyMotionOnWaypoints({
          prompt: prompt.trim(),
          diffusion_steps: steps,
          seed: seedValue,
          waypoints: wps.waypoints.map(w => [w.x, floorY, w.z]),
          pathMode: wps.pathMode,
          walk_speed: walkSpeed,
          constraint_stride: constraintStride,
          constraint_weight: constraintWeight,
          smooth,
          // Stands alone: this clip is SAVED and replayed from arbitrary
          // states, so there is no live facing or pose for it to continue from.
          // It starts directly on the route's own heading rather than easing out
          // of whatever the avatar happened to be facing, and neither its first
          // nor its last frames are pinned to a one-off pose.
          continuity: false,
          ...(passDuration != null ? { duration_sec: passDuration } : {}),
          play: false,
        })
        previewWaypointTracks(res.tracks, res.waypoints[0], res.frameYaw)
        setLastPerWaypoint(res.perWaypoint)
        setLastTracks(res.tracks)
        setLastGen({
          prompt: prompt.trim(),
          seed: seedValue,
          duration_sec: res.durationSec,
          diffusion_steps: steps,
          waypoints: wps.waypoints.map(w => [w.x, w.z]),
          path_mode: wps.pathMode,
          walk_speed: walkSpeed,
          timing_mode: timingMode,
          // From the result, not the input boxes: the generator floors + rounds
          // the stride, so provenance must carry the clip's real settings or a
          // Modify round-trip would reproduce the typo instead of the clip.
          constraint_stride: res.constraintStride,
          constraint_weight: res.constraintWeight,
          smooth: res.smooth,
          // Kept so Preview-again relocates to the same place the generation did.
          startWorld: res.waypoints[0],
          frameYaw: res.frameYaw,
          generatedAt: Date.now(),
        })
      } else {
        const gen = {
          prompt: prompt.trim(),
          duration_sec: durSec,
          diffusion_steps: steps,
          seed: seedValue,
        }
        const tracks = await postGenerate(gen)
        previewTracks(tracks)
        setLastPerWaypoint(null)
        setLastTracks(tracks)
        setLastGen({ ...gen, generatedAt: Date.now() })
      }
    } catch (e) {
      setError(String(e.message ?? e))
    } finally {
      setGenerating(false)
    }
  }

  // Replay the last generation on the avatar — no new kimodo round-trip, no
  // seed re-roll. The preview clip self-clears when it finishes, so this is how
  // the user re-watches the motion they already have before deciding to Save.
  const onPreviewAgain = () => {
    if (!lastTracks) return
    if (lastGen?.waypoints?.length >= 2) previewWaypointTracks(lastTracks, lastGen.startWorld, lastGen.frameYaw)
    else previewTracks(lastTracks)
  }

  // Bake the last generation to a GLB and save it straight to disk — same bytes
  // Save uploads, but no server round-trip. Uses the current name / keepRootMotion
  // / provenance so the downloaded file matches what Save would store.
  const onDownload = async () => {
    if (!lastTracks || !lastGen) { setError('Generate an animation first'); return }
    setDownloading(true); setError(null)
    try {
      await downloadBodyMotionGlb(lastTracks, {
        name: name.trim(),
        keepRootMotion,
        provenance: {
          prompt: lastGen.prompt,
          seed: lastGen.seed,
          duration_sec: lastGen.duration_sec,
          diffusion_steps: lastGen.diffusion_steps,
          generatedAt: lastGen.generatedAt,
          waypoints: lastGen.waypoints ?? null,
          path_mode: lastGen.path_mode ?? null,
          walk_speed: lastGen.walk_speed ?? null,
          timing_mode: lastGen.timing_mode ?? null,
          constraint_stride: lastGen.constraint_stride ?? null,
          constraint_weight: lastGen.constraint_weight ?? null,
          smooth: lastGen.smooth ?? null,
        },
      })
    } catch (e) {
      setError(String(e.message ?? e))
    } finally {
      setDownloading(false)
    }
  }

  const onGenerate = () => {
    const raw = seed.trim()
    if (raw === '') { runGenerate(randomSeed()); return }
    const n = Number(raw)
    // A non-numeric seed would serialize to seed:null (server-random) with no
    // signal that the pin was dropped — reject it loudly instead.
    if (!Number.isFinite(n)) { setError('Seed must be a number (leave blank for random)'); return }
    runGenerate(n)
  }

  const onReroll = () => {
    const s = randomSeed()
    setSeed(String(s)) // surface the fresh seed so the user can reproduce it
    runGenerate(s)
  }

  const onSave = async () => {
    if (!lastTracks || !lastGen) { setError('Generate an animation first'); return }
    if (!name.trim()) { setError('Name is required'); return }
    // Modify + "Save as new" reusing the original's exact name would mint a
    // duplicate row AND — because upload hot-registers by name with replace:true
    // — silently shadow the original clip for every name-based lookup this
    // session. Force a distinct name (or an explicit Replace) instead.
    if (modifyTarget && saveMode === 'new' && name.trim() === modifyTarget.name) {
      setError('Name matches the original. Change the name to save a copy, or choose "Replace original".')
      return
    }
    setSaving(true); setError(null)
    try {
      const buf = await exportBodyMotionGlb(lastTracks, {
        name: name.trim(),
        keepRootMotion,
        provenance: {
          prompt: lastGen.prompt,
          seed: lastGen.seed,
          duration_sec: lastGen.duration_sec,
          diffusion_steps: lastGen.diffusion_steps,
          generatedAt: lastGen.generatedAt,
          waypoints: lastGen.waypoints ?? null,
          path_mode: lastGen.path_mode ?? null,
          walk_speed: lastGen.walk_speed ?? null,
          timing_mode: lastGen.timing_mode ?? null,
          constraint_stride: lastGen.constraint_stride ?? null,
          constraint_weight: lastGen.constraint_weight ?? null,
          smooth: lastGen.smooth ?? null,
        },
      })
      const file = new File([buf], `${name.trim()}.glb`, { type: 'model/gltf-binary' })
      // uploadAnimationAsset hot-registers the clip itself (row.name +
      // custom_body_<id>) by fetching row.file_url — no manual register here.
      const row = await CustomAssetManager.uploadAnimationAsset(file, {
        name: name.trim(),
        kind: 'body',
        category,
        additive,
        loopable,
      })
      assetRegistry.setPath(`anim:${row.id}`, path)

      // Replace original: no AAM file-replace route exists, so replacement is
      // upload-new (done above) → delete-old. Ordered new-first so an upload
      // failure never destroys the original. The new row has a NEW id — the
      // panel's relink warning tells the user references aren't auto-rewritten.
      if (modifyTarget && saveMode === 'replace' && modifyTarget.id !== row.id) {
        try {
          await CustomAssetManager.deleteAnimationAsset(modifyTarget.id)
        } catch (e) {
          // The new clip is already saved; only the old-row cleanup failed.
          // Surface loudly instead of pretending the replace was clean.
          setError(`Saved the new clip, but removing the original failed: ${String(e.message ?? e)}. Delete "${modifyTarget.name}" manually.`)
          setSaving(false)
          return
        }
      }

      setSaving(false)
      onDone?.(row)
    } catch (e) {
      setError(String(e.message ?? e))
      setSaving(false)
    }
  }

  const saveDisabled = busy || !lastTracks || !name.trim()
  // A 2+-waypoint route that failed to plan (e.g. navmesh missing in navmesh
  // mode) can't be generated — block Generate/Re-roll loudly rather than firing
  // a call that would throw.
  const routeBlocked = wpState.waypoints.length >= 2 && !!wpState.route?.error
  const genDisabled = busy || !prompt.trim() || routeBlocked

  const body = (
    <>
      {/* Body — scrollable form. */}
      <div style={{ padding: 14, overflowY: 'auto', flex: 1 }}>
        <label style={labelStyle}>Name</label>
        <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="name (required)" />

        <label style={labelStyle}>Prompt</label>
        <input
          style={inputStyle}
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          placeholder="e.g. a person waves hello"
          autoFocus
        />
        <div style={{ color: tokens.dim, fontFamily: 'var(--m-font)', fontSize: 9.5, marginTop: 4, lineHeight: 1.5 }}>
          Third-person, e.g. "a person waves hello" or "a person crosses their arms".
        </div>

        {/* Waypoints (optional) — a concatenated navmesh route drives one kimodo
            call. Strictly additive: with < 2 waypoints the prompt-only path runs
            unchanged. Collapsed by default since it's optional. */}
        <button
          onClick={() => setWpCollapsed(c => !c)}
          style={wpHeaderStyle}
          title="Route the avatar along a navmesh path. Add 2+ waypoints to drive generation from the route; leave empty for a plain prompt-only motion."
        >
          <span style={{ color: tokens.dim, fontSize: 9 }}>{wpCollapsed ? '▶' : '▼'}</span>
          <span>Waypoints</span>
          <span style={{ color: tokens.dim, textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>(optional)</span>
          {wpState.waypoints.length > 0 && <span style={wpCountBadge}>{wpState.waypoints.length}</span>}
        </button>
        {!wpCollapsed && (
        <div style={wpBoxStyle}>
          {/* Path mode + straight-line warning. Navmesh disabled with no mesh. */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ ...labelStyle, margin: 0 }}>Path</span>
            {['navmesh', 'straight'].map(m => {
              const disabled = m === 'navmesh' && !hasNavmesh
              return (
                <button
                  key={m}
                  onClick={() => !disabled && waypointPickerStore.setPathMode(m)}
                  disabled={disabled}
                  style={{ ...pillStyle(wpState.pathMode === m), opacity: disabled ? 0.35 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                  title={disabled ? 'No navmesh baked for this environment — bake one to route around obstacles' : `Use the ${m} path`}
                >{m}</button>
              )
            })}
          </div>
          {wpState.pathMode === 'straight' && (
            <div style={noticeStyle(tokens.warn)}>
              ⚠ {hasNavmesh ? 'Straight-line mode' : 'No navmesh for this environment'} — obstacles are ignored and walked straight through.
            </div>
          )}

          {/* Add waypoints: viewport pick, avatar start, or manual X/Z. */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            <button
              onClick={() => (wpState.armed ? waypointPickerStore.disarm() : waypointPickerStore.arm())}
              style={pillStyle(wpState.armed)}
              title="Ctrl+left-click in the viewport places a waypoint; Esc disarms."
            >{wpState.armed ? '◉ Picking… (Esc)' : '⌖ Pick in viewport'}</button>
            <button
              onClick={() => {
                try {
                  const p = currentAvatarXZ()
                  const w = { x: p[0], z: p[2], snapped: false, drift: 0 }
                  const list = waypointPickerStore.getState().waypoints
                  waypointPickerStore.setWaypoints(list.length ? [w, ...list.slice(1)] : [w])
                } catch (e) { setError(String(e.message ?? e)) }
              }}
              style={pillStyle(false)}
              title="Seed the first waypoint from the avatar's current position."
            >Avatar as start</button>
          </div>

          {/* Manual X/Z entry. */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
            <input
              style={{ ...inputStyle, width: 72, padding: '4px 8px' }}
              type="number" step="0.1" placeholder="x" value={manualX}
              onChange={e => setManualX(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addManualWaypoint() }}
            />
            <input
              style={{ ...inputStyle, width: 72, padding: '4px 8px' }}
              type="number" step="0.1" placeholder="z" value={manualZ}
              onChange={e => setManualZ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addManualWaypoint() }}
            />
            <button onClick={addManualWaypoint} style={pillStyle(false)} title="Add a waypoint at these world X/Z coordinates">+ Add</button>
            {wpState.waypoints.length > 0 && (
              <>
                <div style={{ flex: 1 }} />
                <button onClick={() => waypointPickerStore.clear()} style={{ ...pillStyle(false), color: tokens.danger, borderColor: tokens.border }} title="Remove all waypoints">Clear</button>
              </>
            )}
          </div>

          {/* Timing: pick ONE of speed-derived duration or an explicit duration. */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
            <span style={{ ...labelStyle, margin: 0 }}>Timing</span>
            <button onClick={() => setTimingMode('speed')} style={pillStyle(timingMode === 'speed')} title="Clip duration = route length ÷ avg speed">By speed</button>
            <button
              onClick={() => {
                setDurationSec(d => durationOnEnterDurationMode({ current: d, derived: derivedDuration, userEdited: durationTouched }))
                setTimingMode('duration')
              }}
              style={pillStyle(timingMode === 'duration')}
              title="Pin an explicit clip duration (the Duration field below); avg speed is ignored"
            >By duration</button>
          </div>

          {/* Avg walk speed — drives the derived clip duration (length / speed).
              Disabled in duration mode (the Duration field takes over). */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, opacity: timingByDuration ? 0.4 : 1 }}>
            <span style={{ ...labelStyle, margin: 0 }}>Avg speed</span>
            <input
              style={{ ...inputStyle, width: 72, padding: '4px 8px' }}
              type="number" min="0.1" step="0.1" value={walkSpeedInput}
              onChange={e => setWalkSpeedInput(e.target.value)}
              disabled={timingByDuration}
              title={timingByDuration ? 'Ignored in duration mode — switch Timing to "By speed" to use it.' : 'Average speed (m/s) used to derive the clip duration from the route length. ~1.3 = natural walk; raise for a jog, lower for a stroll.'}
            />
            <span style={{ fontFamily: 'var(--m-mono)', fontSize: 9.5, color: tokens.dim }}>m/s</span>
            {timingByDuration && <span style={{ fontFamily: 'var(--m-font)', fontSize: 9, color: tokens.dim }}>→ using duration</span>}
          </div>

          {/* Route shape — what the planned polyline looks like BEFORE it becomes
              anchors. The viewport route line redraws on every change, so this
              control previews itself. Disabled in straight mode, where a segment
              is already the straight line these modes exist to recover. */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, opacity: wpState.pathMode === 'straight' ? 0.4 : 1 }}>
            <span style={{ ...labelStyle, margin: 0 }}>Shape</span>
            <button
              onClick={() => setSmooth('none')}
              style={pillStyle(smooth === 'none')}
              disabled={wpState.pathMode === 'straight'}
              title="The raw navmesh route. The grid is 4-connected, so a diagonal comes out as a staircase."
            >Grid</button>
            <button
              onClick={() => setSmooth('curve')}
              style={pillStyle(smooth === 'curve')}
              disabled={wpState.pathMode === 'straight'}
              title={`Round every turn of the route into a Bezier fillet, leaving the straight runs on the navmesh untouched.
Waypoints are turns like any other and get rounded too — the route passes through them rather than pivoting on them.
Only the start and the destination are exact.`}
            >Curved</button>
          </div>

          {/* Advanced: the root2d constraint's two knobs. Behind a toggle because
              they have tuned defaults and are only worth reaching for when a
              generated walk misbehaves — the everyday controls are Shape and the
              waypoint list. */}
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, cursor: 'pointer' }}>
            <input
              data-testid="wp-advanced"
              type="checkbox"
              checked={advanced}
              onChange={e => setAdvanced(e.target.checked)}
            />
            <span style={{ ...labelStyle, margin: 0, cursor: 'pointer' }}>Advanced</span>
          </label>

          {advanced && (<>
          {/* Path tracking — how hard the generated root is pinned to the planned
              polyline. Stride is the anchor spacing in 30 fps frames; weight is
              the root2d CFG scale. */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
            <span style={{ ...labelStyle, margin: 0 }}>Tracking</span>
            <input
              data-testid="wp-constraint-stride"
              style={{ ...inputStyle, width: 56, padding: '4px 8px' }}
              type="number" min="1" step="1" value={strideInput}
              onChange={e => setStrideInput(e.target.value)}
              title={`Anchor stride: send a path anchor every N of the 30 fps frames.
3 (the default) is ten anchors a second — enough to hold the path, loose enough to leave the model its own gait.
1 pins every frame, which is also a per-frame heading command and tends to fight the walk.
Waypoints and the endpoints are always anchored whatever the stride.`}
            />
            <span style={{ fontFamily: 'var(--m-mono)', fontSize: 9.5, color: tokens.dim }}>stride</span>
            <input
              data-testid="wp-constraint-weight"
              style={{ ...inputStyle, width: 56, padding: '4px 8px' }}
              type="number" min="0.01" max="20" step="0.5" value={weightInput}
              onChange={e => setWeightInput(e.target.value)}
              title={`Constraint weight (root2d CFG scale).
1 = the tuned default (kimodo's own is 2). Accepted range is 0 < x <= 20 — zero is rejected by the service, not an unguided run.
The constraint carries facing as well as position, so past ~2 it visibly twists the body.`}
            />
            <span style={{ fontFamily: 'var(--m-mono)', fontSize: 9.5, color: tokens.dim }}>weight</span>
          </div>
          {constraintWeight >= 2 && (
            <div style={{ ...noticeStyle(tokens.warn), marginTop: 6 }}>
              weight ≥ 2 — the constraint carries facing too, and tends to fight the gait here
            </div>
          )}
          </>)}

          {/* Waypoint list — numbered chips matching the viewport markers. */}
          {wpState.waypoints.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {wpState.waypoints.map((w, i) => (
                <div key={i} style={wpRowStyle}>
                  <span style={wpBadgeStyle(i === 0)} title={i === 0 ? 'Start' : `Waypoint ${i}`}>{i}</span>
                  <span style={{ fontFamily: 'var(--m-mono)', fontSize: 11, color: tokens.text }}>
                    <span style={{ color: tokens.dim }}>x</span> {w.x.toFixed(2)}
                    <span style={{ color: tokens.dim, marginLeft: 8 }}>z</span> {w.z.toFixed(2)}
                    {i === 0 && <span style={{ color: WAYPOINT_COLORS.start.hex, marginLeft: 8, fontSize: 9 }}>START</span>}
                  </span>
                  <div style={{ flex: 1 }} />
                  <button onClick={() => waypointPickerStore.moveWaypoint(i, -1)} disabled={i === 0} style={iconBtn(i === 0)} title="Move up">↑</button>
                  <button onClick={() => waypointPickerStore.moveWaypoint(i, 1)} disabled={i === wpState.waypoints.length - 1} style={iconBtn(i === wpState.waypoints.length - 1)} title="Move down">↓</button>
                  <button onClick={() => waypointPickerStore.removeWaypoint(i)} style={iconBtn(false)} title="Remove">✕</button>
                </div>
              ))}
            </div>
          )}

          {wpState.armed && (
            <div style={noticeStyle(WAYPOINT_COLORS.point.hex)}>Ctrl+left-click the ground to place a waypoint · Esc to stop</div>
          )}

          {wpState.pickError && <div style={noticeStyle(tokens.danger)}>{wpState.pickError}</div>}

          {wpState.waypoints.length === 1 && (
            <div style={{ ...noticeStyle(tokens.dim), marginTop: 8 }}>Add one more waypoint — a route needs a start and at least one destination.</div>
          )}

          {wpState.waypoints.length >= 2 && wpState.route && (
            wpState.route.error ? (
              <div style={noticeStyle(tokens.danger)}>route error: {wpState.route.error}</div>
            ) : (() => {
              const speedRaw = wpState.route.length / walkSpeed
              const speedDur = derivedDuration // already clamped; in scope (route has no error here)
              const effDur = timingMode === 'duration' ? Math.max(0.1, Number(durationSec) || 4) : speedDur
              // Anchor count for the CURRENT stride, computed the same way the
              // request will (same nFrames, same waypoint pins) — so the number
              // shown is the number sent, not an estimate that can drift from it.
              const nFrames = Math.max(2, Math.round(effDur * 30))
              const pts = wpState.waypoints.map(w => [w.x, 0, w.z])
              const anchors = constraintFrames(nFrames, {
                stride: constraintStride,
                pinFrames: waypointFrameIndices(wpState.route.path, pts, nFrames),
              }).length
              return (
                <div style={{ ...routeStatStyle, marginTop: 8 }}>
                  <span style={{ color: tokens.text }}>{wpState.route.segments.length} segment{wpState.route.segments.length === 1 ? '' : 's'}</span>
                  <span style={{ color: tokens.dim }}>·</span>
                  <span style={{ color: tokens.text }}>{wpState.route.length.toFixed(1)} m</span>
                  <span style={{ color: tokens.dim }}>→ ~{effDur.toFixed(1)} s{timingMode === 'duration' ? ' (pinned)' : ''}</span>
                  {timingMode === 'speed' && speedDur !== speedRaw && <span style={{ color: tokens.warn }}>(clamped from {speedRaw.toFixed(1)} s)</span>}
                  <span style={{ color: tokens.dim }}>·</span>
                  <span style={{ color: tokens.text }} title={`${anchors} path anchors across ${nFrames} frames at stride ${constraintStride}, weight ${constraintWeight}`}>
                    {anchors}/{nFrames} anchors
                  </span>
                  {smooth !== 'none' && wpState.route.rawLength > wpState.route.length + 0.01 && (
                    <span
                      style={{ color: tokens.dim }}
                      title={`The raw navmesh staircase was ${wpState.route.rawLength.toFixed(1)} m; smoothing recovered the ${wpState.route.length.toFixed(1)} m it stands for.`}
                    >
                      (from {wpState.route.rawLength.toFixed(1)} m)
                    </span>
                  )}
                </div>
              )
            })()
          )}

          {lastPerWaypoint && lastPerWaypoint.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ ...labelStyle, marginTop: 0 }}>Reached</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {lastPerWaypoint.map((p) => (
                  <span key={p.index} style={reachedChip(p.reached)} title={`Passed within ${p.dist.toFixed(2)} m of waypoint ${p.index}`}>
                    {p.index} {p.reached ? '✓' : '✗'} {p.dist.toFixed(2)}m
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle} title={timingBySpeed ? 'Derived from route length ÷ avg speed (speed mode). Switch the Waypoints timing to "duration" to set it explicitly.' : undefined}>
              Duration (s){timingBySpeed && <span style={{ color: tokens.dim }}> · auto</span>}
            </label>
            <input
              style={inputStyle}
              type="number" min="0.1" step="0.1"
              value={timingBySpeed ? (derivedDuration != null ? derivedDuration.toFixed(1) : '') : durationSec}
              onChange={e => { setDurationSec(e.target.value); setDurationTouched(true) }}
              disabled={timingBySpeed}
              title={timingBySpeed ? 'Derived from route length ÷ avg speed. Switch Waypoints timing to "duration" to edit.' : undefined}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={labelStyle}
              title="Number of diffusion denoising steps. More steps = higher fidelity but slower generation."
            >Diffusion steps</label>
            <input
              style={inputStyle}
              type="number"
              min="1"
              value={diffusionSteps}
              onChange={e => setDiffusionSteps(e.target.value)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={labelStyle}
              title="Fixes the random seed so the same prompt reproduces the same motion. Leave blank for a fresh random seed each Generate."
            >Seed (blank = random)</label>
            <input
              style={inputStyle}
              value={seed}
              onChange={e => setSeed(e.target.value)}
              placeholder="random"
            />
          </div>
        </div>

        <label style={labelStyle}>Flags</label>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <Check
            label="In place"
            checked={inPlace}
            onChange={setInPlace}
            title="Plays the clip where it stands: the root stays put for the whole animation. Off (the default) keeps the root motion, stored relative to the start pose (delta per frame), never as absolute world position."
          />
          <Check
            label="Loopable"
            checked={loopable}
            onChange={setLoopable}
            title="Marks the clip as safe to loop — the runtime will repeat it seamlessly instead of playing once."
          />
          <Check
            label="Additive (layered gesture)"
            checked={additive}
            onChange={setAdditive}
            title="Bakes the clip as deltas-from-rest so it can layer on top of the base pose through the gesture channel. Required for anything played as a gesture; leave off for a full-pose body clip that drives the whole skeleton."
          />
        </div>

        {modifyTarget && (
          <>
            <label style={labelStyle}>Save mode</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setSaveMode('new')} style={pillStyle(saveMode === 'new')}>Save as new</button>
              <button onClick={() => setSaveMode('replace')} style={pillStyle(saveMode === 'replace')}>Replace original</button>
            </div>
            {saveMode === 'replace' && (
              <div style={{ marginTop: 6, color: tokens.warn, fontFamily: 'var(--m-font)', fontSize: 9.5, lineHeight: 1.5 }}>
                ⚠ Replacing re-creates the asset with a NEW id ("{modifyTarget.name}" is
                deleted and re-uploaded). Blueprints that reference the original
                clip by id will need relinking — references are NOT rewritten
                automatically.
              </div>
            )}
          </>
        )}

        {/* Generate / Re-roll live on the avatar before committing. Save is
            enabled only after a generation. */}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button onClick={onGenerate} disabled={genDisabled} style={btnStyle(!genDisabled)}>
            {generating ? 'Generating…' : 'Generate'}
          </button>
          <button onClick={onReroll} disabled={genDisabled} style={btnStyle(!genDisabled)}>
            Re-roll
          </button>
          {lastTracks && (
            <button
              onClick={onPreviewAgain}
              disabled={busy}
              style={btnStyle(!busy)}
              title="Replay the last generated motion on the avatar (no re-generate, no seed change)"
            >
              ▶ Preview again
            </button>
          )}
          {lastTracks && (
            <button
              onClick={onDownload}
              disabled={busy}
              style={btnStyle(!busy)}
              title="Download the generated motion as a .glb file (no server upload)"
            >
              {downloading ? 'Preparing…' : '⬇ Download .glb'}
            </button>
          )}
          {lastTracks && !generating && (
            <span style={{ alignSelf: 'center', fontFamily: 'var(--m-font)', fontSize: 10, color: tokens.dim }}>
              previewing — Save to keep
            </span>
          )}
        </div>

        {error && (
          <div style={{ marginTop: 12, color: tokens.danger, fontFamily: 'var(--m-font)', fontSize: 11 }}>
            {error}
          </div>
        )}
      </div>

      {/* Footer — Cancel / Save. */}
      <div style={footerStyle}>
        <button onClick={handleCancel} style={btnSecondary}>Cancel</button>
        <button
          onClick={onSave}
          disabled={saveDisabled}
          style={{ ...btnStyle(!saveDisabled), opacity: saveDisabled ? 0.5 : 1 }}
        >
          {saving ? 'Saving…' : (modifyTarget ? (saveMode === 'replace' ? 'Replace original' : 'Save as new') : 'Save')}
        </button>
      </div>
    </>
  )

  // FloatingWindow owns the chrome in EVERY mode (title bar, ✕, float/dock/
  // pop-out), which is why this panel no longer draws a header of its own — two
  // stacked title bars is the clutter that component exists to remove.
  const windowEl = (
    <FloatingWindow
      id={GENERATE_WINDOW_ID}
      title={modifyTarget ? 'MODIFY BODY ANIMATION' : 'GENERATE BODY ANIMATION'}
      // No barExtra subtitle. "Text prompt → kimodo motion" was decorative — the
      // PROMPT field carries its own third-person hint two rows down — and in a
      // 380px docked region it was the single biggest consumer of a bar that
      // also has to fit three window controls.
      onClose={onCancel ? handleCancel : undefined}
      defaultSize={{ w: 400, h: 660 }}
      // Generate previews the motion live on the avatar, so a centred window
      // would open on top of the very thing it exists to drive (the RIG PREVIEW
      // precedent). Tall and narrow because the form is a single column.
      defaultAnchor="right"
    >
      {body}
    </FloatingWindow>
  )

  // Three hosts, one window. Floating and popped escape any box of ours
  // (position:fixed / a portal), so the wrapper would only reserve an empty
  // `width`-wide gap in the layout for a panel that had moved elsewhere.
  // Docked in the SHELL, the right Inspector panel is already the box. Docked
  // in a standalone content browser (no shell), we still owe it a column.
  if (win.mode !== 'docked' || hosted) return windowEl
  return <aside style={shellStyle(width)}>{windowEl}</aside>
}

function Check({ label, checked, onChange, title }) {
  return (
    <label
      title={title}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: tokens.text, fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer' }}
    >
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

function shellStyle(width) {
  return {
    width, flexShrink: 0,
    background: tokens.bg,
    borderLeft: `1px solid ${tokens.border}`,
    display: 'flex', flexDirection: 'column',
    minHeight: 0,
  }
}

const footerStyle = {
  padding: '12px 14px', borderTop: `1px solid ${tokens.border}`,
  display: 'flex', gap: 8, justifyContent: 'flex-end',
}

const btnSecondary = {
  padding: '6px 14px', borderRadius: 4, fontFamily: 'var(--m-font)', fontSize: 11,
  letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 600,
  background: 'transparent', border: `1px solid ${tokens.border}`,
  color: tokens.dim, cursor: 'pointer',
}

function pillStyle(active) {
  return {
    padding: '4px 12px', borderRadius: 4, fontFamily: 'var(--m-font)', fontSize: 10,
    letterSpacing: '.06em', textTransform: 'uppercase',
    background: active ? tokens.primary : 'transparent',
    border: `1px solid ${active ? tokens.primary : tokens.border}`,
    color: active ? tokens.onPrim : tokens.dim, cursor: 'pointer',
  }
}

function btnStyle(enabled) {
  return {
    padding: '6px 14px', borderRadius: 4, fontFamily: 'var(--m-font)', fontSize: 11,
    letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 600,
    background: enabled ? tokens.primary : 'transparent',
    border: `1px solid ${enabled ? tokens.primary : tokens.border}`,
    color: enabled ? tokens.onPrim : tokens.dim,
    cursor: enabled ? 'pointer' : 'default', opacity: enabled ? 1 : 0.6,
  }
}

// ── Waypoints section styling ────────────────────────────────────────────────
const wpHeaderStyle = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
  marginTop: 10, padding: '4px 0', background: 'transparent', border: 'none',
  cursor: 'pointer', color: tokens.dim,
  fontFamily: 'var(--m-font)', fontSize: 9.5, fontWeight: 700,
  letterSpacing: '.1em', textTransform: 'uppercase', textAlign: 'left',
}
const wpCountBadge = {
  minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: tokens.primary, color: tokens.onPrim,
  fontSize: 9, fontWeight: 700, letterSpacing: 0,
}
// Surface nesting on the wizard panel: the box recedes (--m-bg), its rows sit
// back up on the panel color. Both were pinned to the raw vars while the shell
// was hardcoded dark; with the shell on --m-panel the box would have matched it.
const wpBoxStyle = {
  marginTop: 6, padding: 10, borderRadius: 5,
  background: tokens.bgInset, border: `1px solid ${tokens.border}`,
}
const wpRowStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '4px 6px', borderRadius: 4, background: tokens.bg,
  border: `1px solid ${tokens.border}`,
}
const routeStatStyle = {
  display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
  fontFamily: 'var(--m-mono)', fontSize: 10,
  padding: '5px 8px', borderRadius: 4,
  background: 'color-mix(in srgb, var(--m-accent) 8%, transparent)', border: `1px solid ${tokens.border}`,
}
function noticeStyle(color) {
  return { marginTop: 6, color, fontFamily: 'var(--m-font)', fontSize: 9.5, lineHeight: 1.5 }
}
function wpBadgeStyle(isStart) {
  return {
    flexShrink: 0, width: 18, height: 18, borderRadius: '50%',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--m-mono)', fontSize: 10, fontWeight: 700,
    background: (isStart ? WAYPOINT_COLORS.start : WAYPOINT_COLORS.point).hex, color: WAYPOINT_COLORS.badgeText.hex,
  }
}
function iconBtn(disabled) {
  return {
    background: 'transparent', border: `1px solid ${tokens.border}`,
    color: disabled ? tokens.faint : tokens.dim, borderRadius: 3,
    width: 20, height: 20, fontSize: 10, padding: 0,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
  }
}
function reachedChip(reached) {
  return {
    fontFamily: 'var(--m-font)', fontSize: 9.5, padding: '2px 6px', borderRadius: 3,
    background: `color-mix(in srgb, ${reached ? 'var(--m-online)' : tokens.danger} 12%, transparent)`,
    color: reached ? 'var(--m-online)' : tokens.danger,
    border: `1px solid color-mix(in srgb, ${reached ? 'var(--m-online)' : tokens.danger} 30%, transparent)`,
  }
}
